import consoleLogger from '../../logger/consoleLogger';
import { AClient } from '../aClient';
import { ClientToJsInterface } from '../clientToJsInterface';
import { JsToClientInterface } from '../jsToClientInterface';
import { EmscriptenClientJsInterface } from './emscriptenClientJsInterface';
import { EmscriptenJsInterface } from './emscriptenJsInterface';
import { GameClient, GameClientLoadingState } from '../gameClient';
import { PreloaderScreen } from '../webgl/preloaderScreen';
import { LoadingProgressEventEmitter } from '../loadingHandler/loadingProgressEventEmitter';
import { NormalWebglLoadingHandler } from '../loadingHandler/normalWebglLoadingHandler';
import { getPreloaderPercentsLayout } from '../loadingHandler/preloaderPercentsLayout';

const logger = consoleLogger.getPrefixedCopy('EMSCRIPTEN_CLIENT');

const CONTAINER_SELECTOR = '#gameContainer';
const CANVAS_SELECTOR = `${CONTAINER_SELECTOR} #gameCanvas`;
const MAX_PERCENT = 100;
const CLIENT_SCRIPT = 'game.js';

export class EmscriptenClient extends AClient {
    private static instance: GameClientInstance | null = null;
    private static bootPromise: Promise<void> | null = null;
    private static readyPromise: Promise<void> | null = null;
    private static resolveReady: (() => void) | null = null;

    public static embed(): void {
        if (EmscriptenClient.bootPromise) {
            return;
        }
        EmscriptenClient.bootPromise = EmscriptenClient.boot();
        EmscriptenClient.bootPromise.catch((): void => { });
    }

    private static async boot(): Promise<void> {
        const client = GameClient.getInstance();
        PreloaderScreen.getInstance().show();
        PreloaderScreen.getInstance().showPreloader();

        client.setLoadingState(GameClientLoadingState.LOADING);

        logger.info(`boot(): emscriptenBaseUrl=${window.app.emscriptenBaseUrl}`);

        try {
            const base = EmscriptenClient.baseUrl();
            if (!base) {
                throw new Error('emscriptenBaseUrl is not configured — refusing to boot Emscripten client');
            }
            // game.js applies this to .wasm/.data; the host never touches window.Module.
            const locateFile = (filename: string) => `${base}${filename}`;

            await EmscriptenClient.loadScript(`${base}${CLIENT_SCRIPT}`);
            client.setLoadingState(GameClientLoadingState.EMBEDDED);
            EmscriptenClient.hide();
            $(window).on('resize', () => { EmscriptenClient.stretchClient(); });

            // Same preloader pipeline as Webgl: runtime download fills the compiling range, the client's
            // own setPreloaderProgress fills the client range and flips the state to LOADED at 100%.
            LoadingProgressEventEmitter.addLoadingProgressHandler(new NormalWebglLoadingHandler());
            LoadingProgressEventEmitter.firePreStartEvent(getPreloaderPercentsLayout().initialPercent);

            EmscriptenClient.instance = await window.createGameClient({
                canvas: $(CANVAS_SELECTOR).get(0) as HTMLCanvasElement,
                locateFile,
                onProgress: (percent: number) => {
                    const share = Math.max(0, Math.min(MAX_PERCENT, percent)) / MAX_PERCENT;
                    LoadingProgressEventEmitter.fireCompilingProgressEvent(share);
                }
            });

            EmscriptenClient.stretchClient();
            // The factory resolves when the runtime is up, not when the game is playable — showing the
            // client and hiding the preloader is ShowClientHandler's job, on the client's showClient.
            EmscriptenClient.markReady();
        } catch (error) {
            logger.error('runtime failed to init', error);
            window.Sentry.captureException(error);
            client.setLoadingState(GameClientLoadingState.FAILED);
            PreloaderScreen.getInstance().hide();
            throw error;
        }
    }

    public static sendCommand(method: string, params: object): void {
        if (!EmscriptenClient.instance) {
            logger.warning(`sendCommand('${method}') before instance ready — dropped`);
            return;
        }
        EmscriptenClient.instance.sendCommand(method, params);
    }

    public static show(): void {
        $(CONTAINER_SELECTOR).css('left', '0px');
        EmscriptenClient.stretchClient();
    }

    public static hide(): void {
        $(CONTAINER_SELECTOR).css('left', '-999999px');
    }

    public static setClientSize(_widthPercent: number, _heightPercent: number): void {
        EmscriptenClient.stretchClient();
    }

    public static stretchClient(): void {
        const canvas = $(CANVAS_SELECTOR);
        canvas.css({ display: 'block', margin: 'auto' });
    }

    // One promise for all callers whatever the order against embed(); never rejects, as Webgl's poll.
    public static clientApiReady(): Promise<void> {
        return EmscriptenClient.readyDeferred();
    }

    private static readyDeferred(): Promise<void> {
        if (!EmscriptenClient.readyPromise) {
            EmscriptenClient.readyPromise = new Promise<void>((resolve) => {
                EmscriptenClient.resolveReady = resolve;
            });
        }

        return EmscriptenClient.readyPromise;
    }

    private static markReady(): void {
        EmscriptenClient.readyDeferred();
        EmscriptenClient.resolveReady?.();
    }

    private static baseUrl(): string {
        // Hosting only, never the page origin — nothing to resolve.
        return window.app.emscriptenBaseUrl || '';
    }

    public static getClientToJsInterface(): ClientToJsInterface {
        return new EmscriptenClientJsInterface();
    }

    public static getJsToClientInterface(): JsToClientInterface {
        return new EmscriptenJsInterface();
    }

    private static loadScript(src: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const container = $(CONTAINER_SELECTOR).get(0);
            if (!container) {
                reject(new Error(`${CONTAINER_SELECTOR} not found in DOM`));
                return;
            }
            const script = document.createElement('script');
            script.async = true;
            script.src = src;
            script.onload = () => {
                logger.info(`client script loaded: ${src}`);
                resolve();
            };
            script.onerror = (event) => {
                logger.error(`client script FAILED to load: ${src}`, event);
                reject(new Error(`Failed to load client script: ${src}`));
            };
            logger.info(`injecting client script: ${src}`);
            container.appendChild(script);
        });
    }
}
